import React, { useState } from "react";
import { Download, FileText, Map, Database } from "lucide-react";
import type { ProcessedRoute, RouteExportOptions } from "../../types/route";
import { RouteProcessor } from "../../utils/routeUtils";
import { downloadAsJson } from "../../utils/helpers";

interface ExportControlsProps {
  routes: ProcessedRoute[];
  selectedRoute: ProcessedRoute | null;
}

export const ExportControls: React.FC<ExportControlsProps> = ({
  routes,
  selectedRoute,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [exportOptions] = useState<RouteExportOptions>({
    format: "geojson",
    includeStops: true,
    includeAnalytics: true,
    simplifyRoute: false,
    maxPoints: 1000,
  });

  const handleExport = (format: "gpx" | "geojson" | "csv" | "json") => {
    const routesToExport = selectedRoute ? [selectedRoute] : routes;
    if (routesToExport.length === 0) return;

    switch (format) {
      case "gpx":
        exportGPX(routesToExport);
        break;
      case "geojson":
        exportGeoJSON(routesToExport);
        break;
      case "csv":
        exportCSV(routesToExport);
        break;
      case "json":
        exportJSON(routesToExport);
        break;
    }
  };

  const exportGPX = (routesToExport: ProcessedRoute[]) => {
    routesToExport.forEach((route) => {
      downloadFile(
        RouteProcessor.exportToGPX(route),
        `route-${route.bike_id}.gpx`,
        "application/gpx+xml",
      );
    });
  };

  const exportGeoJSON = (routesToExport: ProcessedRoute[]) => {
    if (routesToExport.length === 1) {
      downloadFile(
        RouteProcessor.exportToGeoJSON(routesToExport[0]),
        `route-${routesToExport[0].bike_id}.geojson`,
        "application/geo+json",
      );
    } else {
      const features = routesToExport.flatMap((route) => {
        const geoJson = JSON.parse(RouteProcessor.exportToGeoJSON(route));
        return geoJson.features;
      });

      downloadFile(
        JSON.stringify({ type: "FeatureCollection", features }, null, 2),
        "routes.geojson",
        "application/geo+json",
      );
    }
  };

  const exportCSV = (routesToExport: ProcessedRoute[]) => {
    const headers = [
      "bike_id",
      "trip_index",
      "split_reason",
      "timestamp",
      "latitude",
      "longitude",
      "speed",
      "battery_level",
    ];

    const rows = routesToExport.flatMap((route) =>
      route.trips.flatMap((trip) =>
        trip.coordinates.map((coord) => [
          route.bike_id,
          trip.trip_index,
          trip.split_reason,
          coord.timestamp,
          coord.latitude,
          coord.longitude,
          coord.speed ?? "",
          coord.battery_level ?? "",
        ]),
      ),
    );

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");

    const dateStr = new Date().toISOString().slice(0, 10);
    const filename =
      routesToExport.length === 1
        ? `route-${routesToExport[0].bike_id}-${dateStr}.csv`
        : `routes-${dateStr}.csv`;

    downloadFile(csvContent, filename, "text/csv");
  };

  const exportJSON = (routesToExport: ProcessedRoute[]) => {
    downloadAsJson(
      {
        exported_at: new Date().toISOString(),
        routes: routesToExport.map((route) => ({
          ...route,
          export_options: exportOptions,
        })),
      },
      routesToExport.length === 1
        ? `route-${routesToExport[0].bike_id}`
        : "routes",
    );
  };

  const downloadFile = (
    content: string,
    filename: string,
    mimeType: string,
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportLabel = selectedRoute
    ? `Export: ${selectedRoute.bike_id}`
    : `Export: ${routes.length} routes`;

  return (
    <div className="relative">
      <button
        onClick={() => setShowOptions(!showOptions)}
        className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
      >
        <Download className="w-4 h-4" />
        <span className="text-sm">Export</span>
      </button>

      {showOptions && (
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setShowOptions(false)}
          />

          <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-[9999]">
            <div className="p-2">
              <div className="text-xs text-gray-500 mb-2">{exportLabel}</div>
              <div className="space-y-1">
                <button
                  onClick={() => handleExport("geojson")}
                  className="flex items-center space-x-2 w-full p-2 hover:bg-gray-100 rounded text-sm"
                >
                  <Map className="w-4 h-4" />
                  <span>GeoJSON</span>
                </button>
                <button
                  onClick={() => handleExport("gpx")}
                  className="flex items-center space-x-2 w-full p-2 hover:bg-gray-100 rounded text-sm"
                >
                  <FileText className="w-4 h-4" />
                  <span>GPX</span>
                </button>
                <button
                  onClick={() => handleExport("csv")}
                  className="flex items-center space-x-2 w-full p-2 hover:bg-gray-100 rounded text-sm"
                >
                  <Database className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={() => handleExport("json")}
                  className="flex items-center space-x-2 w-full p-2 hover:bg-gray-100 rounded text-sm"
                >
                  <FileText className="w-4 h-4" />
                  <span>JSON</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
